import React, { useEffect, useState } from 'react';
import { Card, Upload, Button, List, Image, Popconfirm, Input, Space, message, Breadcrumb, Modal } from 'antd';
import { 
  UploadOutlined, DeleteOutlined, EditOutlined, FileImageOutlined,
  FolderOutlined, FolderAddOutlined, ArrowLeftOutlined 
} from '@ant-design/icons';
import AdminPageLayout from './layout/AdminPageLayout';
import useMediaStore from '../../stores/mediaStore';

const MediaManager = () => {
  const { 
    items, loading, currentPath, setCurrentPath,
    fetchItems, uploadFile, deleteFile, renameFile, createFolder, getFileUrl 
  } = useMediaStore();
  const [editingItem, setEditingItem] = useState(null);
  const [newName, setNewName] = useState('');
  const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  useEffect(() => {
    fetchItems(currentPath);
  }, [currentPath, fetchItems]);

  const handleUpload = async (info) => {
    const { file } = info;
    const result = await uploadFile(file);
    if (result.success) {
      fetchItems(currentPath);
    }
  };

  const handleDelete = async (filename) => {
    const result = await deleteFile(filename);
    if (result.success) {
      fetchItems(currentPath);
    }
  };

  const handleRename = async (oldFilename) => {
    if (!newName) {
      message.error('New name cannot be empty');
      return;
    }
    
    // Preserve file extension for files
    const extension = oldFilename.includes('.') ? `.${oldFilename.split('.').pop()}` : '';
    const newFilename = newName + extension;
    
    const result = await renameFile(oldFilename, newFilename);
    if (result.success) {
      setEditingItem(null);
      setNewName('');
      fetchItems(currentPath);
    }
  };

  const handleCreateFolder = async () => {
    if (newFolderName.trim()) {
      const result = await createFolder(newFolderName, currentPath);
      if (result.success) {
        setIsCreateFolderModalVisible(false);
        setNewFolderName('');
        fetchItems(currentPath);
      }
    }
  };

  const navigateToFolder = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
  };

  const navigateBack = () => {
    const newPath = currentPath.split('/').slice(0, -1).join('/');
    setCurrentPath(newPath);
  };

  const pathParts = currentPath.split('/').filter(Boolean);

  return (
    <AdminPageLayout
      title="Media Manager"
      icon={<FileImageOutlined />}
      subtitle={
        <Breadcrumb
          items={[
            { title: 'Root', onClick: () => setCurrentPath('') },
            ...pathParts.map((part, index) => ({
              title: part,
              onClick: () => setCurrentPath(pathParts.slice(0, index + 1).join('/'))
            }))
          ]}
        />
      }
      actions={
        <Space>
          <Button 
            icon={<FolderAddOutlined />} 
            onClick={() => setIsCreateFolderModalVisible(true)}
          >
            New Folder
          </Button>
          <Upload
            customRequest={handleUpload}
            showUploadList={false}
            accept="image/*"
          >
            <Button type="primary" icon={<UploadOutlined />}>
              Upload Image
            </Button>
          </Upload>
        </Space>
      }
    >
      {currentPath && (
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={navigateBack}
          style={{ marginBottom: 16 }}
        >
          Back
        </Button>
      )}

      <List 
        grid={{ 
          gutter: 16,
          xs: 1,
          sm: 2,
          md: 3,
          lg: 4,
          xl: 6,
          xxl: 8,
        }}
        dataSource={items}
        loading={loading}
        renderItem={item => (
          <List.Item>
            <Card
              hoverable
              onClick={() => item.type === 'folder' && navigateToFolder(item.name)}
              cover={item.type === 'file' ? (
                <div style={{ height: 200, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Image
                    alt={item.name}
                    src={getFileUrl(item.name)}
                    style={{ maxHeight: '100%', objectFit: 'contain' }}
                  />
                </div>
              ) : (
                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FolderOutlined style={{ fontSize: 64 }} />
                </div>
              )}
              actions={[
                <Button 
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingItem(item);
                    setNewName(item.name.split('.')[0]);
                  }}
                />,
                <Popconfirm
                  title={`Delete ${item.type}`}
                  description={`Are you sure you want to delete this ${item.type}?`}
                  onConfirm={(e) => {
                    e.stopPropagation();
                    handleDelete(item.name);
                  }}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button icon={<DeleteOutlined />} danger />
                </Popconfirm>
              ]}
            >
              {editingItem === item.name ? (
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onPressEnter={() => handleRename(item.name)}
                  />
                  <Button type="primary" onClick={() => handleRename(item.name)}>
                    Save
                  </Button>
                  <Button onClick={() => {
                    setEditingItem(null);
                    setNewName('');
                  }}>
                    Cancel
                  </Button>
                </Space.Compact>
              ) : (
                <Card.Meta 
                  title={item.name}
                  style={{ textAlign: 'center' }}
                />
              )}
            </Card>
          </List.Item>
        )}
      />

      <Modal
        title="Create New Folder"
        open={isCreateFolderModalVisible}
        onOk={handleCreateFolder}
        onCancel={() => {
          setIsCreateFolderModalVisible(false);
          setNewFolderName('');
        }}
      >
        <Input
          placeholder="Folder name"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          prefix={<FolderOutlined />}
        />
      </Modal>
    </AdminPageLayout>
  );
};

export default MediaManager;
